import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { CONFLICT_REFRESH_MESSAGE, isConflictError } from '@/common/helpers/errorHelpers'

const DEFAULT_OPTIONS = { variant: 'error', autoHideDuration: 5000 }

/**
 * Hook that surfaces RTK Query mutation errors as snackbars, with special
 * handling for HTTP 409 Conflict (optimistic-concurrency) responses.
 *
 * On a 409 the user is told to refresh the page and try again; every other
 * error keeps its caller-provided `fallbackMessage`.
 * @returns {{ showMutationError: (error: unknown, fallbackMessage: string, options?: object) => string }}
 *   `showMutationError` enqueues the appropriate snackbar and returns the message shown.
 * @example
 * const { showMutationError } = useMutationErrorSnackbar()
 * try {
 *   await updateOrder(payload).unwrap()
 * } catch (error) {
 *   showMutationError(error, t('$t(An error occurred) updating the order.'))
 * }
 */
export const useMutationErrorSnackbar = () => {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    const showMutationError = (error, fallbackMessage, options = {}) => {
        const message = isConflictError(error) ? t(CONFLICT_REFRESH_MESSAGE) : fallbackMessage
        enqueueSnackbar(message, { ...DEFAULT_OPTIONS, ...options })
        return message
    }

    return { showMutationError }
}
